import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { z } from 'zod';
import {
    FormSection,
    MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
    FilterConfig,
    MasterDataTable,
    TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { VendorType } from '../types/vendorType';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const VendorTypeManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingVendorType, setEditingVendorType] = useState<VendorType | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch vendor types with server-side filtering and pagination
  const {
    data: vendorTypesResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'vendorTypes',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/vendorType?${params.toString()}`
      );
      const result = response.data.data || {};
      console.log('VendorType API response:', result);

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const vendorTypes = vendorTypesResponse?.data || [];
  const pagination = vendorTypesResponse?.pagination;

  // Show debugging info
  React.useEffect(() => {
    console.log('VendorTypes API Response:', vendorTypesResponse);
    console.log('VendorTypes data:', vendorTypes);
    console.log('VendorTypes pagination:', pagination);
    console.log('VendorTypes length:', vendorTypes.length);
    console.log('Is vendorTypes array?', Array.isArray(vendorTypes));

    // Check individual vendor type object
    if (vendorTypes.length > 0) {
      console.log('First vendorType object:', vendorTypes[0]);
      console.log('VendorType keys:', Object.keys(vendorTypes[0]));
    }
  }, [vendorTypesResponse, vendorTypes, pagination]);

  // Create vendor type mutation
  const createVendorTypeMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/vendorType', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendorTypes'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'vendorType'],
      });
      toast({
        title: 'Vendor Type created',
        description: 'Vendor Type has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingVendorType(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error creating vendor type',
          description:
            error.response?.data?.error?.message ||
            error.response?.data?.error ||
            'Failed to create vendor type',
          variant: 'destructive',
        });
      }
    },
  });

  // Update vendor type mutation
  const updateVendorTypeMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(`/api/master-data/vendorType/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendorTypes'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'vendorType'],
      });
      toast({
        title: 'Vendor Type updated',
        description: 'Vendor Type has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingVendorType(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error updating vendor type',
          description:
            error.response?.data?.error?.message ||
            error.response?.data?.error ||
            'Failed to update vendor type',
          variant: 'destructive',
        });
      }
    },
  });

  // Delete vendor type mutation
  const deleteVendorTypeMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/vendorType/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendorTypes'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'vendorType'],
      });
      toast({
        title: 'Vendor Type deleted',
        description: 'Vendor Type has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error deleting vendor type',
        description: error.response?.data?.error || 'Failed to delete vendor type',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration with better responsive design
  const columns: TableColumn<VendorType>[] = [
    {
      key: 'name',
      label: 'Vendor Type Name',
      sortable: true,
      render: (value, vendorType) => (
        <div className='space-y-1'>
          <div className='font-medium text-sm'>{vendorType.name}</div>
          <div className='text-xs text-muted-foreground font-mono'>
            {vendorType.code}
          </div>
        </div>
      ),
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      hideOnTablet: true,
      render: (value, vendorType) => (
        <div className='font-mono text-sm bg-muted px-2 py-1 rounded'>
          {vendorType.code}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, vendorType) => (
        <Badge
          variant={vendorType.status === 'Active' ? 'default' : 'secondary'}
          className={
            vendorType.status === 'Active'
              ? 'bg-green-100 text-green-800 hover:bg-green-100'
              : 'bg-red-100 text-red-800 hover:bg-red-100'
          }
        >
          {vendorType.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (value, vendorType) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(vendorType.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      sortable: true,
      hideOnTablet: true,
      render: (value, vendorType) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(vendorType.updatedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Enhanced filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [{ value: 'all', label: 'All Statuses' }, ...statusOptions],
      placeholder: 'Filter by status',
    },
  ];

  // Form sections configuration with better layout
  const formSections: FormSection[] = [
    {
      title: 'Vendor Type Information',
      description: 'Configure vendor type details and settings.',
      fields: [
        {
          name: 'name',
          label: 'Vendor Type Name',
          type: 'text',
          required: true,
          placeholder: 'Enter vendor type name (e.g., "Courier Service", "Freight Forwarder")',
          description: 'A unique descriptive name for this vendor type',
          validation: z
            .string()
            .min(2, 'Name must be at least 2 characters')
            .max(100, 'Name cannot exceed 100 characters')
            .regex(
              /^[a-zA-Z0-9\s\-_().]+$/,
              'Name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses'
            ),
        },
        {
          name: 'code',
          label: 'Vendor Type Code',
          type: 'text',
          required: true,
          placeholder: 'Enter vendor type code (e.g., "COURIER", "FREIGHT")',
          description: 'A unique short code for this vendor type (uppercase letters, numbers, underscores and hyphens only)',
          validation: z
            .string()
            .min(2, 'Code must be at least 2 characters')
            .max(20, 'Code cannot exceed 20 characters')
            .regex(
              /^[A-Z0-9_-]+$/,
              'Code can only contain uppercase letters, numbers, underscores and hyphens'
            ),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' },
          ],
          description: 'Set the availability status for this vendor type',
        },
      ],
    },
  ];

  const handleCreate = () => {
    setEditingVendorType(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (vendorType: VendorType) => {
    setEditingVendorType(vendorType);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (vendorType: VendorType) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete "${vendorType.name}"? This action cannot be undone.`
      )
    ) {
      deleteVendorTypeMutation.mutate(vendorType._id);
    }
  };

  const handleSubmit = (data: any) => {
    // Transform form data for better API compatibility
    const transformedData = {
      ...data,
      // Convert string 'Active'/'Inactive' to proper status if needed
      status: data.status,
      // Ensure code is uppercase
      code: data.code?.toUpperCase(),
    };

    if (editingVendorType) {
      updateVendorTypeMutation.mutate({
        id: editingVendorType._id,
        data: transformedData,
      });
    } else {
      createVendorTypeMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingVendorType(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['vendorTypes'] });
    toast({
      title: 'Data refreshed',
      description: 'Vendor Type data has been refreshed successfully.',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      // Add current search and filters to export
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/vendorType/export?${params.toString()}`,
        {
          responseType: 'blob',
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `vendor_types_${timestamp}.xlsx`);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      // Clean up and remove the link
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Export successful',
        description: 'Vendor Type data has been exported successfully.',
      });
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description:
          error.response?.data?.error || 'Failed to export vendor type data',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            Vendor Type Management
          </h1>
          <p className='text-muted-foreground'>
            Manage vendor type master data with comprehensive controls
          </p>
        </div>
      </div>

      <MasterDataTable<VendorType>
        title='Vendor Types'
        description='Manage and configure vendor type information'
        data={vendorTypes}
        columns={columns}
        isLoading={isLoading}
        // Pagination
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Search
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search vendor types by name or code...'
        // Filtering
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        // Sorting
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        // Actions
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        // Permissions
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingVendorType ? 'Edit Vendor Type' : 'Create Vendor Type'}
        sections={formSections}
        initialData={editingVendorType}
        onSubmit={handleSubmit}
        isLoading={
          createVendorTypeMutation.isPending || updateVendorTypeMutation.isPending
        }
        mode={editingVendorType ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default VendorTypeManagement;

